import { createApp } from "vue";
import { createPinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import { Vue3ProgressPlugin } from "@marcoschulte/vue3-progress";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";
import Toast, { POSITION } from "vue-toastification";
import type { PluginOptions } from "vue-toastification";
import mitt from "mitt";
import { IonicVue } from "@ionic/vue";
import { addIcons } from "ionicons";

/* CSS base de Bootstrap */
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

/* CSS de librerías (ANTES del tema) */
import "vue3-perfect-scrollbar/style.css";
import "@marcoschulte/vue3-progress/dist/index.css";
import "vue-toastification/dist/index.css";

/* Font Awesome (antes del tema) */
import "@fortawesome/fontawesome-free/scss/fontawesome.scss";
import "@fortawesome/fontawesome-free/scss/regular.scss";
import "@fortawesome/fontawesome-free/scss/solid.scss";
import "@fortawesome/fontawesome-free/scss/brands.scss";
import "@fortawesome/fontawesome-free/scss/v4-shims.scss";

/* Tema / overrides (ÚLTIMO SIEMPRE) */
import "./scss/vue.scss";

/* JS de Bootstrap (el bundle ya trae Popper) */
import "bootstrap/dist/js/bootstrap.bundle.min.js";

// Iconos de Ionic
import {
  addOutline,
  calendar,
  calendarOutline,
  todayOutline,
  arrowBackOutline,
  playOutline,
  pauseOutline,
  listOutline,
  stopOutline,
  walkOutline,
  returnUpBackOutline,
  reload,
  search,
  closeOutline,
  checkmarkDoneOutline,
  alertCircleOutline,
} from "ionicons/icons";

addIcons({
  "add-outline": addOutline, // icono "+"
  "arrow-back-outline": arrowBackOutline, // icono "volver"
  "play-outline": playOutline, // icono "play"
  "pause-outline": pauseOutline, // icono "pausa"
  "list-outline": listOutline, // icono "lista" (mis incidencias)
  "walk-outline": walkOutline, // icono "caminar (salir)"
  "return-up-back-outline": returnUpBackOutline, // icono "volver"
  "stop-outline": stopOutline, // icono "stop"
  "refresh-outline": reload, // icono refrescar
  search: search, // icono de búsqueda
  "calendar-outline": calendarOutline, // icono de calendario
  calendar: calendar, // icono de calendario
  "today-outline": todayOutline, // icono para "día"
  "close-outline": closeOutline, // icono para "cerrar"
  "alert-circle-outline": alertCircleOutline, // icono para alertas
  "checkmark-done-outline": checkmarkDoneOutline, // icono para marcar todo como leído
});

import App from "./App.vue";
import router from "./router";

import Panel from "@/components/bootstrap/Panel.vue";
import PanelBody from "@/components/bootstrap/PanelBody.vue";
import PanelHeader from "@/components/bootstrap/PanelHeader.vue";
import PanelTitle from "@/components/bootstrap/PanelTitle.vue";
import PanelFooter from "@/components/bootstrap/PanelFooter.vue";
import PanelToolbar from "@/components/bootstrap/PanelToolbar.vue";

// ============================================================================
// 🔧 PWA: registro de Service Worker + helpers para el banner (2 PWAs: normal / kiosk)
//    ✅ Un único flujo de actualización (sin confirm() nativo)
//    ✅ Flags sessionStorage namespaced + legacy
//    ✅ Instalación robusta: espera a worker en waiting antes de SKIP_WAITING
//    ✅ Recovery iOS/Safari: fallback por versión y hard reload si controllerchange no llega
// ============================================================================
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  // ───────────────────────────────────────────────────────────────────────────
  // Helpers sessionStorage (iOS-safe)
  // ───────────────────────────────────────────────────────────────────────────
  const ssGet = (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const ssSet = (key: string, val: string | null) => {
    try {
      if (val === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, val);
    } catch {
      // ignore
    }
  };

  // ✅ helper: set/clear BOTH (namespaced + legacy)
  const ssSetBoth = (
    namespacedKey: string,
    legacyKey: string,
    val: string | null,
  ) => {
    ssSet(namespacedKey, val);
    ssSet(legacyKey, val);
  };

  // Reload más fiable en iOS/PWA que window.location.reload()
  const isEditableElementActive = () => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return false;

    const shadowActive = active.shadowRoot?.activeElement;

    return Boolean(
      active.matches("input, textarea, select, ion-input, ion-textarea, ion-select") ||
        active.isContentEditable ||
        shadowActive?.matches("input, textarea, select"),
    );
  };

  const isLoginPath = () => window.location.pathname === "/login";

  const shouldDeferHardReload = () =>
    document.visibilityState === "visible" && isEditableElementActive();

  const markDeferredHardReload = () => {
    ssSet("pwa:reload-deferred", "1");
    ssSet("pwa:reload-deferred-at", String(Date.now()));
    window.dispatchEvent(new CustomEvent("pwa:need-refresh"));
  };

  const hardReload = () => {
    if (shouldDeferHardReload()) {
      markDeferredHardReload();
      return;
    }

    const url = window.location.href;
    window.location.href = url;
  };

  window.addEventListener("load", async () => {
    // detecta si estamos en /kiosk/ o en la app normal
    const isKiosk = window.location.pathname.startsWith("/kiosk");
    const swPath = isKiosk ? "/kiosk-sw.js" : "/sw.js";
    const scope = isKiosk ? "/kiosk/" : "/";

    // ✅ Namespace flags para que no se pisen entre PWAs
    const prefix = isKiosk ? "kiosk" : "main";
    const FLAG_UPDATING = `${prefix}:pwa:updating`;
    const FLAG_UPDATING_AT = `${prefix}:pwa:updating-at`;
    const FLAG_JUST_UPDATED = `${prefix}:pwa:just-updated`;

    // ✅ NUEVO: bloqueo cuando el usuario rechaza (evita UI/reload fantasma)
    const FLAG_BLOCK_AUTO = `${prefix}:pwa:block-auto`;

    // ✅ Legacy (por si el banner dispara antes de resolver flags)
    const LEGACY_UPDATING = "pwa:updating";
    const LEGACY_UPDATING_AT = "pwa:updating-at";
    const LEGACY_JUST_UPDATED = "pwa:just-updated";
    const LEGACY_BLOCK_AUTO = "pwa:block-auto";
    const UPDATE_FLAG_TTL_MS = 2 * 60 * 1000;

    // ✅ FIX iOS: “recovery” por versión (si controllerchange no llega)
    //    - Solo actúa si updating=1 (usuario aceptó instalar)
    //    - Si la versión cambió respecto a la última vista => la update se aplicó
    const LAST_VERSION_KEY = `${prefix}:pwa:last-version`;

    const isFreshUpdateIntent = () => {
      const raw = ssGet(FLAG_UPDATING_AT) || ssGet(LEGACY_UPDATING_AT);
      if (!raw) return false;

      const startedAt = Number(raw);
      const age = Date.now() - startedAt;
      return (
        Number.isFinite(startedAt) && age >= 0 && age <= UPDATE_FLAG_TTL_MS
      );
    };

    const clearUpdateIntent = () => {
      ssSetBoth(FLAG_UPDATING, LEGACY_UPDATING, null);
      ssSetBoth(FLAG_UPDATING_AT, LEGACY_UPDATING_AT, null);
    };

    try {
      const current = __APP_VERSION__;
      const prev = localStorage.getItem(LAST_VERSION_KEY);

      const wasUpdating =
        ssGet(FLAG_UPDATING) === "1" || ssGet(LEGACY_UPDATING) === "1";
      const freshUpdateIntent = isFreshUpdateIntent();

      const blocked =
        ssGet(FLAG_BLOCK_AUTO) === "1" || ssGet(LEGACY_BLOCK_AUTO) === "1";

      if (wasUpdating && !freshUpdateIntent) {
        clearUpdateIntent();
      }

      // Si estaba “instalando” y NO está bloqueado, pero la versión ya cambió,
      // damos por aplicada la actualización (iOS se saltó controllerchange).
      if (
        wasUpdating &&
        freshUpdateIntent &&
        !blocked &&
        prev &&
        prev !== current
      ) {
        clearUpdateIntent();
        ssSetBoth(FLAG_JUST_UPDATED, LEGACY_JUST_UPDATED, "1");
      }

      localStorage.setItem(LAST_VERSION_KEY, current);
    } catch {
      // ignore
    }

    try {
      const reg = await navigator.serviceWorker.register(swPath, {
        scope,
        type: "module",
        updateViaCache: "none",
      });
      console.log("SW registrado en", swPath, reg);

      // ─────────────────────────────────────────────────────────────────────────
      // Helpers internos
      // ─────────────────────────────────────────────────────────────────────────
      const dispatchNeedRefresh = () => {
        window.dispatchEvent(new CustomEvent("pwa:need-refresh"));
      };

      const attachInstallingListener = (worker: ServiceWorker) => {
        worker.addEventListener("statechange", () => {
          if (
            worker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            dispatchNeedRefresh();
          }
        });
      };

      // 🧰 Helpers globales para el banner
      (window as any).__pwa = {
        flags: {
          updating: FLAG_UPDATING,
          updatingAt: FLAG_UPDATING_AT,
          justUpdated: FLAG_JUST_UPDATED,
          // opcional: legacy para inspección
          legacyUpdating: LEGACY_UPDATING,
          legacyUpdatingAt: LEGACY_UPDATING_AT,
          legacyJustUpdated: LEGACY_JUST_UPDATED,
          // opcional: bloqueo
          blockAuto: FLAG_BLOCK_AUTO,
          legacyBlockAuto: LEGACY_BLOCK_AUTO,
        },

        checkNow: async () => {
          await reg.update();

          if (reg.waiting) {
            dispatchNeedRefresh();
            return;
          }

          if (reg.installing) {
            attachInstallingListener(reg.installing);
          } else {
            setTimeout(() => {
              if (reg.installing) attachInstallingListener(reg.installing);
            }, 200);
          }
        },

        // ✅ Instalación bajo demanda (botón del banner / confirm)
        //    - arma flags "updating"
        //    - limpia bloqueo
        //    - manda SKIP_WAITING al WAITING (no installing)
        applyUpdateNow: async () => {
          // ✅ Usuario acepta: elimina bloqueo previo
          ssSetBoth(FLAG_BLOCK_AUTO, LEGACY_BLOCK_AUTO, null);

          // ✅ Persistimos “instalando” (namespaced + legacy)
          ssSetBoth(FLAG_UPDATING, LEGACY_UPDATING, "1");
          ssSetBoth(FLAG_UPDATING_AT, LEGACY_UPDATING_AT, String(Date.now()));
          ssSetBoth(FLAG_JUST_UPDATED, LEGACY_JUST_UPDATED, null);

          // ✅ SKIP_WAITING al worker correcto: WAITING
          if (reg.waiting) {
            reg.waiting.postMessage({ type: "SKIP_WAITING" });
          } else {
            // best-effort: fuerza update y espera un pelín a que pase a waiting
            try {
              await reg.update();
            } catch {}
            setTimeout(() => {
              reg.waiting?.postMessage({ type: "SKIP_WAITING" });
            }, 150);
          }

          // Fallback iOS: si controllerchange no llega rápido, forzamos reload duro
          setTimeout(() => {
            if (
              ssGet(FLAG_UPDATING) === "1" ||
              ssGet(LEGACY_UPDATING) === "1"
            ) {
              hardReload();
            }
          }, 1500);
        },

        hardReload,

        get version() {
          return __APP_VERSION__;
        },
        get semver() {
          return __SEMVER__;
        },
      };

      // ✅ señal para el banner: __pwa ya existe
      window.dispatchEvent(new CustomEvent("pwa:ready"));

      // Si ya hay un SW nuevo esperando al registrar
      if (reg.waiting) {
        dispatchNeedRefresh();
      }

      // Detecta nuevas instalaciones (mantiene tu confirm)
      let askedForThisUpdate = false;

      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (askedForThisUpdate) return;

          if (
            newWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            askedForThisUpdate = true;

            const aceptar = confirm(
              "Hay una nueva versión disponible. ¿Actualizar ahora?",
            );

            if (aceptar) {
              // ✅ Acepta -> usa la ruta oficial (arma flags + skipWaiting)
              (window as any).__pwa?.applyUpdateNow?.();

              // fallback ultra-best-effort si __pwa aún no existiese
              if (!(window as any).__pwa?.applyUpdateNow) {
                ssSetBoth(FLAG_BLOCK_AUTO, LEGACY_BLOCK_AUTO, null);
                ssSetBoth(FLAG_UPDATING, LEGACY_UPDATING, "1");
                ssSetBoth(
                  FLAG_UPDATING_AT,
                  LEGACY_UPDATING_AT,
                  String(Date.now()),
                );
                ssSetBoth(FLAG_JUST_UPDATED, LEGACY_JUST_UPDATED, null);
                reg.waiting?.postMessage({ type: "SKIP_WAITING" });
              }
            } else {
              // ✅ Rechaza -> BLOQUEA cualquier UI/reload aunque el SW cambie controller por su cuenta
              ssSetBoth(FLAG_BLOCK_AUTO, LEGACY_BLOCK_AUTO, "1");
              clearUpdateIntent();
              ssSetBoth(FLAG_JUST_UPDATED, LEGACY_JUST_UPDATED, null);

              window.dispatchEvent(new CustomEvent("pwa:need-refresh"));
            }
          }
        });
      });

      // Primera instalación → listo offline
      if (!navigator.serviceWorker.controller) {
        window.dispatchEvent(new CustomEvent("pwa:offline-ready"));
      }

      // ✅ CLAVE: controllerchange SOLO actúa si:
      //    - NO está bloqueado (usuario no rechazó)
      //    - y estaba armado "updating" (usuario aceptó)
      let reloading = false;

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloading) return;

        const blocked =
          ssGet(FLAG_BLOCK_AUTO) === "1" || ssGet(LEGACY_BLOCK_AUTO) === "1";

        const updating =
          ssGet(FLAG_UPDATING) === "1" || ssGet(LEGACY_UPDATING) === "1";
        const freshUpdateIntent = isFreshUpdateIntent();

        // ❌ Usuario rechazó: NO hacemos nada
        if (blocked) return;

        // ❌ No estaba armado el update: NO hacemos nada
        if (!updating || !freshUpdateIntent) {
          if (updating) clearUpdateIntent();
          return;
        }

        reloading = true;

        clearUpdateIntent();
        ssSetBoth(FLAG_JUST_UPDATED, LEGACY_JUST_UPDATED, "1");

        hardReload();
      });
    } catch (err) {
      console.error("Error al registrar el SW:", err);
    }
  });

  // ========================================================================
  // ⏳ Comprobación automática de actualizaciones de PWA (igual que lo tenías)
  // ========================================================================
  let pwaCheckInterval: ReturnType<typeof setInterval> | undefined;

  function startPwaAutoCheck() {
    if (pwaCheckInterval !== undefined) return;

    (window as any).__pwa?.checkNow?.();

    pwaCheckInterval = setInterval(
      () => {
        (window as any).__pwa?.checkNow?.();
      },
      60 * 60 * 1000,
    );
  }

  function stopPwaAutoCheck() {
    if (pwaCheckInterval !== undefined) {
      clearInterval(pwaCheckInterval);
      pwaCheckInterval = undefined;
    }
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !isEditableElementActive()) {
      startPwaAutoCheck();
    } else stopPwaAutoCheck();
  });

  window.addEventListener("focus", () => {
    if (isEditableElementActive()) return;
    (window as any).__pwa?.checkNow?.();
  });

  if (document.visibilityState === "visible") startPwaAutoCheck();

  window.addEventListener("beforeunload", () => stopPwaAutoCheck());
}

const emitter = mitt();
const app = createApp(App);

app.component("Panel", Panel);
app.component("PanelBody", PanelBody);
app.component("PanelHeader", PanelHeader);
app.component("PanelFooter", PanelFooter);
app.component("PanelToolbar", PanelToolbar);
app.component("PanelTitle", PanelTitle);

// IONIC
// Importar los estilos de Ionic
import "@ionic/vue/css/core.css";
import "@ionic/vue/css/normalize.css";
import "@ionic/vue/css/structure.css";
import "@ionic/vue/css/typography.css";
import "@ionic/vue/css/padding.css";
import "@ionic/vue/css/float-elements.css";
import "@ionic/vue/css/text-alignment.css";
import "@ionic/vue/css/text-transformation.css";
import "@ionic/vue/css/flex-utils.css";
import "@ionic/vue/css/display.css";

// toast (vue-tostification)
const options: PluginOptions = {
  position: POSITION.TOP_LEFT,
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false,
  transition: "Vue-Toastification__slideBlurred",
  maxToasts: 20,
  newestOnTop: true,
};

// SYNCFUSION
import { registerLicense } from "@syncfusion/ej2-base";

// Registering Syncfusion license key
registerLicense(
  "Ngo9BigBOggjHTQxAR8/V1NBaF5cXmZCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWXteeHRTRWJYVkJyW0A=",
);
// traducciones
import { createI18n } from "vue-i18n";
// importamos los ficheros con las traducciones
import { messages } from "./lang/traductions";
// instancia y objeto de configuración
export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  allowComposition: true,
  // entorno local de trabajo del usuario
  locale: "es",
  // indica que pasa si hay un texto que no existe en el idioma solicitado, en este caso lo buscará en inglés
  fallbackLocale: "es",
  // archivos con el conjunto de traducciones
  messages,
});

const pinia = createPinia();
pinia.use(createPersistedState());
app.use(pinia);

// MOCKS
if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_MOCKS === "true") {
  await Promise.all([
    import("@/stores/mocks/checkTime/setupCheckTimeMocks"),
    import("@/stores/mocks/messenger/setupMessengerMocks"),
    import("@/stores/mocks/management/setupManagementMocks"),
  ]);
}

app.use(router);
app.use(Vue3ProgressPlugin);
app.component("PerfectScrollbar", PerfectScrollbar);
app.use(i18n);
app.use(IonicVue);
app.use(Toast, options);
app.config.globalProperties.emitter = emitter;

// stores
import { useAuthStore } from "@/stores/auth";
import { useAppOptionStore } from "@/stores/app-option";
const authStore = useAuthStore();
const appOption = useAppOptionStore();
// se agrega una escucha para el evento beforeunload y se hace logout cuando el usuario cierra la pestaña o la ventana,
// o recarga la página, pero sólo si no eligió recordar en el login
window.addEventListener("beforeunload", async () => {
  // ✅ Guarda lastPath SOLO si es una ruta “útil” (evita loops)
  const path = window.location.pathname || "/";
  const full = path + window.location.search + window.location.hash;

  if (path !== "/" && path !== "/login") {
    localStorage.setItem("lastPath", full);
  } else {
    // opcional: si quieres limpiar para no reutilizar basura
    localStorage.removeItem("lastPath");
  }

  localStorage.setItem("selectedMenu", appOption.appSelectedMenu);
  localStorage.setItem("selectedMenuIcon", appOption.appSelectedMenuIcon);

  const rememberSetup = JSON.parse(
    localStorage.getItem("rememberSetup") || "false",
  );

  if (!rememberSetup) {
    const storedCompany = localStorage.getItem("kiosk-company");
    const storedApiUrl = localStorage.getItem("kiosk-api-url");
    await authStore.logOut();
    localStorage.setItem("company", appOption.appCompany);
    localStorage.setItem("apiURL", appOption.appApiURL);
    localStorage.setItem("kiosk-company", storedCompany ?? "");
    localStorage.setItem("kiosk-api-url", storedApiUrl ?? "");
    localStorage.setItem("rememberSetup", "false");
    return;
  } else {
    localStorage.setItem("reloadDetected", "true");
    if (appOption.appCompany !== "" && appOption.appApiURL !== "") {
      localStorage.setItem("company", appOption.appCompany);
      localStorage.setItem("apiURL", appOption.appApiURL);
    }
  }
});

app.mount("#app");

/*
===============================================================================
📌 FLUJO DE beforeunload (recarga / cierre / pestaña)
===============================================================================

┌────────────────────────────────────────────────────────────┐
│ EVENTO: beforeunload (recarga / cierre ventana / pestaña)  │
└────────────────────────────────────────────────────────────┘
               │
               ▼
  Guarda valores en localStorage:
  - lastPath = ruta actual
  - selectedMenu = menú actual
  - selectedMenuIcon = icono menú actual
               │
               ▼
  Recupera rememberSetup (true / false)
               │
       ┌───────┴────────┐
       │                │
       ▼                ▼
 rememberSetup = true    rememberSetup = false
       │                │
       │                │
  (El usuario quiere     (El usuario NO quiere
   recordar sesión)       recordar sesión)
       │                │
       │                │
Guarda:                 ┌───────────────────────────────┐
- reloadDetected = true │ Realiza:                      │
- company / apiURL      │ - Logout                      │
(si existen)            │ - Guarda company / apiURL     │
                        │ - Guarda kiosk-company / api  │
                        │ - rememberSetup = false       │
                        └───────────────────────────────┘
       │
       ▼
 El router comprobará token tras recarga

 ===============================================================================
*/
